import axios from 'axios';
import { config } from '../../../config';
import storeProvider from '../../../storeProvider';
import { setSessionIdToUrl } from 'h-commons';
import { getAndStoreAnalyticsAuthToken } from '@hiver/analytics-auth';
import {
  A_GET,
  A_POST,
  A_PUT,
  A_DELETE,
  A_PATCH,
} from '@hiver/hiver-fetch-lib';
import {
  setConfigKey,
  getConfigKey,
  deleteConfig,
} from '@hiver/hiver-config-library';
import { makeAxiosCall } from "@hiver/bifrost-js";
import refreshModalConsumer from '../components/RefreshModal';
import { BUDDY_ANALYTICS_ENV, BUDDY_LOCAL_ENV, BUDDY_LOCAL_API_BASE_URL, BUDDY_ANALYTICS_API_BASE_URL, BUDDY_LOCAL_V2_URL } from '../constants';
import { getEnv } from '..';

let API_ROOT_URL = config.API_ROOT_URL;
let SLA_ROOT_URL = config.SLA_ROOT_URL;
let NEW_AUTOMATIONS_BASE_API_URL = config.NEW_AUTOMATIONS_BASE_API_URL;
let newAnalyticsAPIBaseUrl = config.NEW_ANALYTICS_API_BASE_URL;
let appEnv = config.APP_ENV;
const MAX_RETRY_AMT = 3;
const CONNECT_ROOT_URL = config.CONNECT_SERVICE_URI;
const JWT_TOKEN_RETRY_DELAY = [1000, 2000, 4000];
const MSG_JWT_TOKEN_EXPIRE = ['expired_token: JWT token is expired.', 'invalid_token: Decoding error !'];
const MSG_CONFIRM_REFRESH = 'Something went wrong. Please try again.';

if (window.hiver_config) {
  API_ROOT_URL = window.hiver_config.V2_API_URL || API_ROOT_URL;
  NEW_AUTOMATIONS_BASE_API_URL =
    window.hiver_config.NEW_AUTOMATIONS_BASE_API_URL ||
    NEW_AUTOMATIONS_BASE_API_URL;
    newAnalyticsAPIBaseUrl =
    window.hiver_config.NEW_ANALYTICS_API_BASE_URL ||
    newAnalyticsAPIBaseUrl;
  SLA_ROOT_URL = window.hiver_config.SLA_ROOT_URL || SLA_ROOT_URL;
}

if (appEnv == BUDDY_LOCAL_ENV) {
  API_ROOT_URL = BUDDY_LOCAL_V2_URL;
}

export const AUTOMATIONS_AUTH_URL = '/api/automationsAuth/auth';
const SLA_AUTH_URL = '/api/slaAuth/auth';

let hiverToken = null;
const bifrostInstance = window.bifrostInstance;

/**
 *
 * @param {Object} inputs Contains the type of http request 'get'/'post' etc
 * @param {String} URL url for the http call
 * @param {Object} data the post request parameters
 */
function getRequestParams(inputs, url, data, headers = null) {
  if (!inputs.type) inputs.type = 'get';
  if (inputs.type.toLowerCase() === 'get') {
    return {
      method: inputs.type,
      url,
      withCredentials: true,
      params: data,
      headers,
    };
  }
  return headers
    ? {
        method: inputs.type,
        url,
        data,
        withCredentials: true,
        headers, 
      }
    : {
        method: inputs.type,
        url,
        data,
        withCredentials: true,
      };
}

const confirmRefresh = () => {
  refreshModalConsumer();
};

const getToken = async (refetch = -1) => {
  try {
    const store = storeProvider.getStore();
    const { appModes, userDetails } = store.getState();
    let userSession = appModes.userSession;
    if (!userSession) {
      userSession = userDetails.userId;
    }
    const response = await fetch(
      `${API_ROOT_URL}/token?usersession=${userSession}`
    );
    const json = await response.json();
    hiverToken = json.token;
    if (!hiverToken) {
      if (refetch > 2) {
        confirmRefresh();
      } else {
        refetch++;
        setTimeout(() => {
          getToken(refetch);
        }, JWT_TOKEN_RETRY_DELAY[refetch]);
      }
    } else {
      return hiverToken;
    }
  } catch (ex) {
    console.log(ex);
    confirmRefresh();
  }
};

/**
 * Wrapper around axios which adds hiver api root url and csrf token by default to the http call
 * @param {Object} inputs contains keys(url, params, callback, onfail) required to make a http call
 */
const HiverAxios = async (inputs, isTest = false, isJWTType = false, retryCount = 0 ) => {
  let data;
  const store = storeProvider.getStore();
  const { appModes } = store.getState();
  const meta = document.querySelector('meta[name="csrf-token"]');
  const csrfToken = meta
    ? meta.getAttribute('content')
    : 'raraasndkadaad.fake_token';
  if (inputs.formData) {
    inputs.params.append('_csrf_token', csrfToken);
    data = inputs.params;
  } else {
    const fields = { _csrf_token: csrfToken };
    data = Object.assign(fields, inputs.params);
  }
  if (!isJWTType) {
    //normal axios flow
    let url = API_ROOT_URL + inputs.url;
    url = setSessionIdToUrl(url, appModes.userSession);
    url = encodeURI(url);
    if (isTest) return axios(getRequestParams(inputs, url, data));
    axios(getRequestParams(inputs, url, data))
      .then((response) => {
        inputs.callback(response);
      })
      .catch((response) => {
        if (inputs.onfail) {
          inputs.onfail(response);
        }
      });
  } else {
    //jwt type api flow
    if (!hiverToken) {
      if (retryCount === 0) {
        await getToken();
      } else {
        confirmRefresh();
      }
    }
    // if we dont supply root_url default it to connect url because there is no other usecase right now
    let url = (inputs.root_url || CONNECT_ROOT_URL) + inputs.url;
    url = setSessionIdToUrl(url, appModes.userSession);
    url = encodeURI(url);
    const headers = {
      authorization: `Bearer ${hiverToken}`,
    };
    axios(getRequestParams(inputs, url, data, headers))
      .then((response) => {
        inputs.callback(response);
      })
      .catch(async (error) => {
        //  The following block will be executed if the JWT token is expired
        if (error.response &&
          error.response.data &&
          MSG_JWT_TOKEN_EXPIRE.includes(error.response.data.message)
        ) {
          //Retry to get the token if it is expired
          //Then call same function
          if (retryCount === 0) {
            await getToken();
            HiverIntegrationAxios(inputs, retryCount++);
            return;
          } else {
            confirmRefresh();
          }
        } else if (inputs.onfail) {
          inputs.onfail(error);
          return;
        }
      });
  }
}

const HiverIntegrationAxios = async (inputs, retryCount = 0) => {
  return HiverAxios(inputs, false, true, retryCount);
};

const HiverAutomationAxios = async (inputs, setUnauthorised) => {
  const config_key = 'automations_auth_token';
  let automations_auth_token = getConfigKey(config_key);
  if (!automations_auth_token) {
    const emailId = getConfigKey('emailId');
    const usersession = getConfigKey('usersession');
    automations_auth_token = await fetchApiAuthToken(
      emailId,
      usersession,
      config_key,
      AUTOMATIONS_AUTH_URL
    );
  }

  if (!automations_auth_token && setUnauthorised) {
    setUnauthorised(true);
    return {};
  }
  const headers = {
    Authorization: 'Bearer ' + automations_auth_token,
  };

  const data = inputs.params || {};
  const url = encodeURI(NEW_AUTOMATIONS_BASE_API_URL + inputs.url);
  const timeout = inputs.timeout || 0;
  return await axios({
    ...getRequestParams(inputs, url, data, headers),
    timeout,
  });
};

const setAuthTokenInHeader = (token, oldHeaders = {}) => {
  return {
    ...oldHeaders,
    Authorization: 'Bearer ' + token,
  };
};

const slaDatafetcher = (
  { params, onSuccess, onFail, config_key, slaAuthUrl },
  retryCount = 0
) => {
  if (retryCount >= MAX_RETRY_AMT) {
    return;
  }

    axios(params).then(response => {
        onSuccess && onSuccess(response);
    }).catch((error) => {
        if(error.response && error.response.status === 403){
            deleteConfig(config_key);
            fetchApiAuthToken(getConfigKey('emailId'), getConfigKey('usersession'), config_key, slaAuthUrl)
            .then((newToken) => {
                //  retrying with new token
                const newParams = {...params};
                if(newParams.headers){
                    newParams.headers = setAuthTokenInHeader(newToken, newParams.headers)
                }
                slaDatafetcher({params:newParams, onSuccess, onFail, config_key, slaAuthUrl}, retryCount +1);
            });
        } else {
            onFail && onFail(error.response);
        }
    });
};

const HiverSlaAxios = async (inputs, setUnauthorised) => {
  const config_key = 'sla_auth_token';
  let sla_auth_token = getConfigKey(config_key);
  if (!sla_auth_token) {
    const emailId = getConfigKey('emailId');
    const usersession = getConfigKey('usersession');
    sla_auth_token = await fetchApiAuthToken(
      emailId,
      usersession,
      config_key,
      SLA_AUTH_URL
    );
  }

  if (!sla_auth_token && setUnauthorised) {
    setUnauthorised(true);
    return {};
  }
  const headers = setAuthTokenInHeader(sla_auth_token);
  const data = inputs.params || {};
  const url = encodeURI(SLA_ROOT_URL + inputs.url);
  const timeout = inputs.timeout || 0;

  slaDatafetcher({
    params: { ...getRequestParams(inputs, url, data, headers), timeout },
    onSuccess: inputs.callback,
    onFail: inputs.onfail,
    config_key: config_key,
    slaAuthUrl: SLA_AUTH_URL,
  });
};

const fetchApiAuthToken = async (
  emailId,
  usersession,
  config_key,
  auth_url
) => {
  const data = { email: emailId, usersession };
  const url = encodeURI(API_ROOT_URL + auth_url);
  try {
    const response = await axios(getRequestParams({ type: 'GET' }, url, data));
    const token = response.data && response.data.token;
    if (!token) {
      deleteConfig(config_key);
      throw new Error(`AuthToken: ${token}`);
    }
    setConfigKey(config_key, token);
    return token;
  } catch (error) {
    console.log(`unable to fetch api_auth_token for ${config_key} :`, error);
  }
};

const HiverAnalyticsAxios = async (inputs) => {
  const lsToken = getConfigKey('analytics_token');
  if (!lsToken) {
    const emailId = getConfigKey('emailId');
    setConfigKey('email', emailId);
    setConfigKey('BASE_URL', config.v2url);
    const authToken = await getAndStoreAnalyticsAuthToken();
    if (authToken) {
      setConfigKey('analytics_token', authToken);
    }
  }

  // In buddy-analytics only, the base url should be set to BUDDY_ANALYTICS_API_BASE_URL
  if (appEnv === BUDDY_ANALYTICS_ENV) {
    newAnalyticsAPIBaseUrl = BUDDY_ANALYTICS_API_BASE_URL;
  } else if (appEnv === BUDDY_LOCAL_ENV) {
    newAnalyticsAPIBaseUrl = BUDDY_LOCAL_API_BASE_URL;
  }
  
  const BaseUrlPath = `${newAnalyticsAPIBaseUrl}/api`;
  const url = encodeURI(BaseUrlPath + inputs.url);
  let response;
  switch (inputs.type) {
    case 'GET':
      response = await A_GET(url);
      break;
    case 'POST':
      response = await A_POST(url, JSON.stringify(inputs.params));
      break;
    case 'PUT':
      response = await A_PUT(url, inputs.params);
      break;
    case 'PATCH':
      response = await A_PATCH(url, inputs.params);
      break;
    case 'DELETE':
      response = await A_DELETE(url);
      break;
    default:
      break;
  }
  return response;
};

const HiverNewAuthCall = ({
  rootUrl,
  hiverhqRootUrl,
  configHiver,
  withCredentials
  })=>{

  const makeNewAuthAxiosCall = async (inputs) => {
      const {app_env, tier} = getEnv();
      const {url,method} = inputs;
      const isHiverApiDomain = typeof inputs?.isHiverApiDomain == 'boolean' ? inputs?.isHiverApiDomain : true;
      const serviceRootUrl = (isHiverApiDomain ? rootUrl : hiverhqRootUrl) + (inputs?.prefix || '');

      const requestParams = {
          root_url: serviceRootUrl,
          url,
          method,
          withCredentials,
          app_env,
          tier,
          headers:inputs?.headers,
          csrfToken:inputs?.csrfToken,
          onSuccess:inputs?.onSuccess,
          onFail:inputs?.onFail,
          data:inputs?.data,
          params:inputs?.params,
          timeout:inputs?.timeout,
          formData:inputs?.formData,
          userId: window.hiver.userid,
          usergroupId: window.hiver.usergroupid,
      };
      try{
          console.log("--- requestParams - ", requestParams, " --- configHiver - ", configHiver, " --- isHiverApiDomain - ", isHiverApiDomain);
          const response = await makeAxiosCall(requestParams, {...configHiver, isHiverApiDomain}); 
          console.log("--- response - ", response);
          return response;
      } 
      catch (error) {
          console.error('API call failed with error: ', error); 
          throw error; 
      }
  }

  return makeNewAuthAxiosCall
  
}


const HiverIntegrationBifrostAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: config.CONNECT_SERVICE_AUTH_URI,
  hiverhqRootUrl: config.CONNECT_SERVICE_AUTH_HIVERHQ_DOMAIN_URI,
  withCredentials: true
})

const getEnvVal = () => {
  //Buddy QA Env
  let tier = 'sit';
  // Production Env
  if (process.env.NODE_ENV.toLowerCase() === 'production' && !config.CI_MODE){
    tier = 'production';
  } 
  else if (config.APP_ENV.toLowerCase() === 'dev') {
    // Buddy-CLI env
    tier = 'dev';
  }
  return {tier}
}

const { tier } = getEnvVal();
const automationUrl = tier === 'dev' ? config.AUTOMATIONS_BUDDY_CLI_URL : config.AUTOMATIONS_NEW_URL;
const automationHiverhqUrl = tier === 'dev' ? config.AUTOMATIONS_BUDDY_CLI_URL : config.AUTOMATIONS_NEW_HIVERHQ_DOMAIN_URL

const HiverAutomationsAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: automationUrl,
  hiverhqRootUrl: automationHiverhqUrl,
  withCredentials: true
})

const slaUrl = tier === 'dev' ? config.NEW_SLA_ROOT_URL_BUDDY_CLI : config.NEW_SLA_ROOT_URL;
const slaHiverhqUrl = tier === 'dev' ? config.NEW_SLA_ROOT_URL_BUDDY_CLI : config.NEW_SLA_HIVERHQ_DOMAIN_URL;

const HiverSlaNewAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: slaUrl,
  hiverhqRootUrl: slaHiverhqUrl,
  withCredentials: true
});

const HiverCustomerPortalNewAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: config.CUSTOMER_PORTAL_ROOT_URL,
  withCredentials: true
})

const customFieldsRootUrl = tier === 'dev' ? config.CUSTOM_FIELDS_URL_BUDDY_CLI : config.CUSTOM_FIELDS_ROOT_URL;

const HiverCustomFieldNewAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: customFieldsRootUrl,
  withCredentials: true
})


const HiverChatBifrostAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: config.CHANNELS_MW_AUTH_URL,
  hiverhqRootUrl: config.CHANNELS_MW_AUTH_HIVERHQ_DOMAIN_URL,
  withCredentials: true
});

const scheduleManagementUrl = tier === 'dev' ? config.SCHEDULE_MANAGEMENT_BUDDY_CLI_URL : config.SCHEDULE_MANAGEMENT_ROOT_URL;
const scheduleManagementHiverhqUrl = tier === 'dev' ? config.SCHEDULE_MANAGEMENT_BUDDY_CLI_URL : config.SCHEDULE_MANAGEMENT_HIVERHQ_ROOT_URL

const HiverOutOfOffice = HiverNewAuthCall({
  configHiver: {
    getToken: true,
  },
  rootUrl: scheduleManagementUrl,
  hiverhqRootUrl: scheduleManagementHiverhqUrl,
  withCredentials: true
});

const HiverMultiKb = HiverNewAuthCall({
  configHiver: {
    getToken: true,
  },
  rootUrl: API_ROOT_URL,
  hiverhqRootUrl: API_ROOT_URL,
  withCredentials: true
});

const CSAT_BASE_ENDPOINT = "/api/v1";
const csatBaseURL =
  (tier === "dev"
    ? config.CSAT_NEW_AUTH_BUDDY_CLI_ROOT_URL
    : config.CSAT_NEW_AUTH_ROOT_URL) + CSAT_BASE_ENDPOINT;

const HiverCsatNewAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: csatBaseURL,
  withCredentials: true,
});


const approvalsBaseUrl = tier === "dev" ? config.APPROVALS_BUDDY_CLI_URL : config.APPROVALS_NEW_AUTH_URL;
const approvalsHiverhqUrl = tier === 'dev' ? config.APPROVALS_BUDDY_CLI_URL : config.APPROVALS_NEW_HIVERHQ_AUTH_URL;

const HiverApprovalNewAuth = HiverNewAuthCall({
  configHiver:{
    getToken: true,
  },
  rootUrl: approvalsBaseUrl,
  hiverhqRootUrl: approvalsHiverhqUrl,
  withCredentials: true,
});




const aiBaseUrl = tier === 'dev' ? config.AI_SERVICE_NEW_AUTH_BUDDY_CLI_ROOT_URL : config.AI_SERVICE_NEW_AUTH_ROOT_URL;
const HiverAiAxios = HiverNewAuthCall({
    configHiver:{
      getToken: true,
    },
    rootUrl: aiBaseUrl,
    withCredentials: true,
    origin: 'AI',
})


export {
  HiverAxios,
  HiverAutomationAxios,
  fetchApiAuthToken,
  HiverAnalyticsAxios,
  HiverChatBifrostAuth,
  HiverSlaAxios,
  HiverIntegrationAxios,
  HiverIntegrationBifrostAuth,
  HiverSlaNewAuth,
  HiverCustomerPortalNewAuth,
  HiverNewAuthCall,
  HiverAutomationsAuth,
  HiverOutOfOffice,
  HiverCustomFieldNewAuth,
  HiverMultiKb,
  HiverCsatNewAuth,
  HiverApprovalNewAuth,
  HiverAiAxios
};
